import { ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { DateRangeFilter, endOfDayParam } from '@/components/ui/date-range-filter';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import type { ApiError } from '@/core/api';
import { CRM_OWNER_ROLES } from '@/features/crm/constants';
import { MemberSelect } from '@/features/members';
import { useUrlState } from '@/hooks/useUrlState';
import { formatIsoDate } from '@/lib/datetime';
import { ToolbarSelect } from '@/components/ui/list-filters';
import {
  BID_CHANNELS,
  BID_ROLLUP_DIMENSIONS,
  channelLabel,
  rateBandLabel,
  rollupDimensionLabel,
  vsMedianLabel,
  type BidRollupDimension,
} from '../constants';
import { useBidRollup } from '../queries';
import type { BidRollupRow } from '../types';

const DEFAULT_GROUP_BY = 'week';

/**
 * The weekly roll-up the Upwork rate-experiment tracker reads: per ISO week
 * (and any extra split) over the FULL filtered set — totals come from the
 * server, never from a page (MEM-0002). Group-by lives in the URL so a view
 * can be shared.
 */
export function BidsRollupPage() {
  const navigate = useNavigate();
  const [groupBy, setGroupBy] = useUrlState('group_by', DEFAULT_GROUP_BY);
  const [channel, setChannel] = useUrlState('channel', 'all');
  const [owner, setOwner] = useUrlState('owner', '');
  const [from, setFrom] = useUrlState('from', '');
  const [to, setTo] = useUrlState('to', '');

  const known = BID_ROLLUP_DIMENSIONS.map((d) => d.value);
  const isDim = (d: string): d is BidRollupDimension => (known as string[]).includes(d);
  const dims = groupBy.split(',').filter(isDim);
  const { data, isPending, isError, error } = useBidRollup({
    group_by: groupBy,
    channel: channel === 'all' ? undefined : channel,
    owner: owner ? Number(owner) : undefined,
    submitted_after: from || undefined,
    submitted_before: endOfDayParam(to),
  });

  function toggleDim(dim: BidRollupDimension) {
    const next = dims.includes(dim) ? dims.filter((d) => d !== dim) : [...dims, dim];
    // Keep the backend's dimension order so the URL is canonical.
    const ordered = known.filter((d) => next.includes(d));
    setGroupBy(ordered.length ? ordered.join(',') : DEFAULT_GROUP_BY);
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate('/crm/bids')} aria-label="Back to bids">
          <ArrowLeft />
        </Button>
        <h1 className="text-2xl font-semibold tracking-tight">Bids · weekly roll-up</h1>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <span className="text-muted-foreground text-sm">Group by</span>
        {BID_ROLLUP_DIMENSIONS.map((d) => (
          <Button
            key={d.value}
            type="button"
            size="sm"
            variant={dims.includes(d.value) ? 'default' : 'outline'}
            aria-pressed={dims.includes(d.value)}
            onClick={() => toggleDim(d.value)}
          >
            {d.label}
          </Button>
        ))}
        <span className="mx-2 hidden h-6 border-l sm:inline" />
        <ToolbarSelect
          value={channel}
          onChange={setChannel}
          allLabel="All channels"
          options={BID_CHANNELS}
          ariaLabel="Channel"
        />
        <MemberSelect
          value={owner}
          onChange={setOwner}
          placeholder="All bidders"
          queryKey="bids-rollup-owner"
          roles={CRM_OWNER_ROLES}
          clearable
          triggerClassName="h-9 w-auto min-w-28"
        />
        <DateRangeFilter
          label="Submitted"
          idPrefix="rollup-submitted"
          from={from}
          to={to}
          onFromChange={setFrom}
          onToChange={setTo}
        />
      </div>

      {isError ? (
        <p className="text-destructive text-sm">{(error as unknown as ApiError).message}</p>
      ) : isPending ? (
        <Skeleton className="h-64 w-full" />
      ) : (
        <>
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
            <Stat label="Proposals" value={data.totals.proposals} />
            <Stat
              label="Replied"
              value={data.totals.replied}
              hint={pct(data.totals.replied, data.totals.proposals)}
            />
            <Stat
              label="Hired"
              value={data.totals.hired}
              hint={pct(data.totals.hired, data.totals.proposals)}
            />
            <Stat
              label="Connects / reply"
              value={data.totals.cost_per_reply ?? '—'}
              hint={`${data.totals.connects_net} net Connects`}
            />
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Per {dims.map(rollupDimensionLabel).join(' × ')}</CardTitle>
              <CardDescription>
                Replied counts replied, interviewing and hired. Costs are net Connects (spent −
                refunded) per outcome.
              </CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    {dims.map((d) => (
                      <TableHead key={d}>{rollupDimensionLabel(d)}</TableHead>
                    ))}
                    <Num>Proposals</Num>
                    <Num>Viewed</Num>
                    <Num>Replied</Num>
                    <Num>Interviewing</Num>
                    <Num>Hired</Num>
                    <Num>Connects</Num>
                    <Num>/ reply</Num>
                    <Num>/ hire</Num>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.rows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={dims.length + 8} className="text-muted-foreground py-10 text-center">
                        No bids in this range.
                      </TableCell>
                    </TableRow>
                  ) : (
                    data.rows.map((row, i) => (
                      <TableRow key={i}>
                        {dims.map((d) => (
                          <TableCell key={d} className="font-medium">
                            {cell(row, d)}
                          </TableCell>
                        ))}
                        <NumCell>{row.proposals}</NumCell>
                        <NumCell>{row.viewed}</NumCell>
                        <NumCell>{row.replied}</NumCell>
                        <NumCell>{row.interviewing}</NumCell>
                        <NumCell>{row.hired}</NumCell>
                        <NumCell>{row.connects_net}</NumCell>
                        <NumCell>{row.cost_per_reply ?? '—'}</NumCell>
                        <NumCell>{row.cost_per_hire ?? '—'}</NumCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}

function cell(row: BidRollupRow, dim: BidRollupDimension): string {
  switch (dim) {
    case 'week':
      return row.week ? `Week of ${formatIsoDate(row.week)}` : '—';
    case 'channel':
      return row.channel ? channelLabel(row.channel) : '—';
    case 'owner':
      return row.owner_name ?? (row.owner ? `User ${row.owner}` : 'Unassigned');
    case 'rate_band':
      return row.rate_band ? rateBandLabel(row.rate_band) : '—';
    case 'bid_vs_median':
      return row.bid_vs_median ? vsMedianLabel(row.bid_vs_median) : 'No median';
    case 'boosted':
      return row.boosted ? 'Boosted' : 'Not boosted';
  }
}

function pct(part: number, whole: number) {
  return whole ? `${Math.round((part / whole) * 100)}% of proposals` : undefined;
}

function Stat({ label, value, hint }: { label: string; value: number | string; hint?: string }) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardDescription>{label}</CardDescription>
        <CardTitle className="text-3xl tabular-nums">{value}</CardTitle>
      </CardHeader>
      {hint ? <CardContent className="text-muted-foreground pt-0 text-xs">{hint}</CardContent> : null}
    </Card>
  );
}

function Num({ children }: { children: React.ReactNode }) {
  return <TableHead className="text-right">{children}</TableHead>;
}
function NumCell({ children }: { children: React.ReactNode }) {
  return <TableCell className="text-right tabular-nums">{children}</TableCell>;
}
